import {
  EMAIL_PROBLEM,
  firstInstallmentIsDue,
  formatMoney,
  installmentsOf,
  isEmailAddress,
  SUGGESTED_INSTALLMENTS,
  whyPlanRefused,
} from '@app/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useId, useRef, useState } from 'react'
import { useTRPC } from '../lib/trpc'
import { continueLabel } from '../lib/two-step-payment'
import {
  Button,
  Choice,
  DateInput,
  Field,
  Notice,
  PaymentStep,
  SettingBlock,
  TextInput,
  Toggle,
  TotalLine,
  useCheckoutConfirmation,
} from '../ui'
import { Challenge } from '../ui/Challenge'
import './pledge.css'

const money = formatMoney

const today = () => new Date().toISOString().slice(0, 10)

/**
 * What somebody undertakes, rather than pays.
 *
 * It answers two things the donate form cannot. A **dedication** at a level a
 * card will not take — the aron kodesh is not a payment, it is an undertaking
 * the office records. And a **gift above the ceiling**, which is the same shape:
 * a figure that size is a commitment rather than a typo, so the page offers to
 * spread it instead of refusing it.
 *
 * **Nothing here charges anything by itself.** The pledge is recorded and the
 * shul answers it — unless the visitor pays the first installment on the spot,
 * which is a stronger statement of intent than any form and confirms it
 * outright.
 *
 * The challenge is what guards it: this write costs the sender nothing and
 * takes a dedication off the board, so the card cannot be the gate the way it
 * is for a gift.
 */
export function PledgeForm({
  orgId,
  campaignId,
  chargeItemId,
  amountCents,
  forLabel,
  donorName,
  donorEmail,
  onCancel,
  onCheckoutComplete,
}: {
  orgId: string
  /** The campaign year they were on, where they were on one. */
  campaignId?: string
  /** The dedication they picked. Absent for an open pledge. */
  chargeItemId?: string
  /** The whole undertaking, settled before this opens. */
  amountCents: number
  /** What it is for, in the words the page used. */
  forLabel: string
  /** What the donor already told the page about themselves, where the flow
   *  reached it. Asking a second time is the seam showing. */
  donorName?: string
  donorEmail?: string
  onCancel: () => void
  onCheckoutComplete: (checkoutId: string) => void
}) {
  const trpc = useTRPC()
  const [name, setName] = useState(donorName ?? '')
  const [email, setEmail] = useState(donorEmail ?? '')
  /* Whether the email field has been left, so what is wrong with it is said
     once somebody has finished typing rather than on the first keystroke. */
  const [emailLeft, setEmailLeft] = useState(false)
  const [phone, setPhone] = useState('')
  const [spread, setSpread] = useState(true)
  /* A count rather than a rate: somebody decides how long to spread it over,
     and the figure per payment follows. */
  const [installments, setInstallments] = useState('12')
  const [cadence, setCadence] = useState<'monthly' | 'annual'>('monthly')
  /* Today, because the ordinary answer is to start it now — and it is a value
     the visitor can move rather than one the form decides for them. */
  const [startsOn, setStartsOn] = useState(today)
  /* Three ways a plan can start, and they are one question rather than two
     toggles: paying now, leaving a card for later, or neither. Only the first
     confirms the pledge without the office. */
  const [start, setStart] = useState<'pay' | 'card' | 'later'>('later')
  const [token, setToken] = useState<string | null>(null)
  const [spentAt, setSpentAt] = useState(0)
  const [done, setDone] = useState(false)
  const confirmation = useCheckoutConfirmation({
    onComplete: onCheckoutComplete,
  })
  const planId = useId()
  /* The checkout this visitor went Back from. The next Continue names it, so
     the same answers reuse the pledge already recorded and changed ones
     withdraw it, rather than a second pledge reaching the office. */
  const left = useRef<string | null>(null)

  const challenge = useQuery(trpc.turnstile.siteKey.queryOptions())
  const pledge = useMutation({
    ...trpc.donation.pledge.mutationOptions(),
    onSuccess: (r) => {
      left.current = null
      if (!r.card) setDone(true)
    },
    /* A token is good once, whether the submit was taken or refused: a spent
       one left in place fails the next Continue, which Back makes likely. */
    onSettled: () => {
      setToken(null)
      setSpentAt(Date.now())
    },
  })

  const count = Number(installments)
  /* The refusal is computed from the toggle rather than from a count that
     parses, so an emptied box states why the commit is held rather than quietly
     turning a plan the form is still showing into a single payment. */
  const refused = !spread
    ? null
    : !Number.isInteger(count) || count < 2
      ? 'Spread it over two payments or more, or turn this off to pay it in one go.'
      : whyPlanRefused(amountCents, count)
  const planned = spread && refused === null
  const split = planned ? installmentsOf(amountCents, count) : null
  /* Paying now is offered whether or not it is spread: a pledge somebody means
     to settle in one go is still one the money can confirm, and the figure is
     then the whole of it. */
  const firstCents = split ? split.rateCents : amountCents
  /* Paying the first installment now means it is due now. A plan starting later
     has nothing payable today, so the card is all the page can ask for — and
     the offer goes rather than being shown and then refused. */
  const startsLater = planned && !firstInstallmentIsDue(startsOn, today())
  const payNow = start === 'pay' && !startsLater
  const named = name.trim().length > 0
  // An address is optional here, since a phone will do, but one given must be whole.
  const emailOk = !email.trim() || isEmailAddress(email)
  const reachable = email.trim().length > 0 || phone.trim().length > 0
  const challenged = !challenge.data?.siteKey || token !== null
  const ready = named && reachable && emailOk && challenged && refused === null

  if (done) {
    return (
      <div className="pledge">
        <Notice tone="info">
          Thank you — {forLabel} is noted against your name. We will be in touch
          to confirm it.
        </Notice>
        <Button variant="secondary" onClick={onCancel}>
          Back
        </Button>
      </div>
    )
  }

  /* A pledge that pays now or leaves a card opens a checkout, and its card
     step draws itself in the place of the answers; the form stays mounted
     underneath, so Back finds the plan exactly as it was set. */
  const leaveCard = start === 'card' || (startsLater && start === 'pay')
  const opensCheckout = payNow || leaveCard
  let payment: ReactNode = null
  if (pledge.data?.card && pledge.data.checkoutId && pledge.variables) {
    const v = pledge.variables
    const sessionFirstCents = v.plan
      ? installmentsOf(v.amountCents, v.plan.installments).rateCents
      : v.amountCents
    payment = (
      <PaymentStep
        orgId={orgId}
        card={pledge.data.card}
        what={forLabel}
        who={`Pledging as ${[v.donorName, v.donorEmail].filter(Boolean).join(', ')}`}
        amount={money(v.payFirstNow ? sessionFirstCents : v.amountCents)}
        amountNote={
          v.payFirstNow
            ? v.plan
              ? `first of ${v.plan.installments} payments`
              : undefined
            : 'pledged, nothing charged now'
        }
        payLabel={
          v.payFirstNow && v.plan
            ? `Pledge ${money(v.amountCents)} and pay ${money(sessionFirstCents)}`
            : `Pledge ${money(v.amountCents)}`
        }
        fine={
          v.payFirstNow
            ? undefined
            : 'Nothing is charged now. We will be in touch to arrange it.'
        }
        confirmation={confirmation}
        onBack={() => {
          left.current = pledge.data?.checkoutId ?? null
          pledge.reset()
          confirmation.reset()
        }}
      />
    )
  }

  return (
    <div className="pledge">
      {/* What is being undertaken, stated before anything is asked: the figure
          is settled by this point and is not a question on this form. The name
          is the form's title and is marked as one, under the page's own — the
          largest words in a region are how a reader finds what they are
          answering, and a span says that to the eye alone. */}
      <div className="pledge__what">
        <h2 className="pledge__what-name" dir="auto">
          {forLabel}
        </h2>
        <span className="pledge__what-sum">{money(amountCents)}</span>
      </div>

      <div className="pledge__donor">
        <Field label="Your name">
          <TextInput
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field
          label="Email"
          problem={
            emailLeft && email.trim() && !isEmailAddress(email)
              ? EMAIL_PROBLEM
              : null
          }
        >
          <TextInput
            type="email"
            onBlur={() => setEmailLeft(true)}
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Phone">
          <TextInput
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </Field>
      </div>
      {/* One of the two, which is the shul's ability to answer rather than a
          format rule. Which one is theirs to choose. */}
      {!reachable && (name.length > 0 || email.length > 0) && (
        <p className="pledge__fine">
          Leave an email address or a phone number, so we can reach you.
        </p>
      )}

      <SettingBlock
        title="Spread it over payments"
        description="A pledge this size is usually paid in installments. Off, we will be in touch about paying it in one go."
      >
        <Toggle
          checked={spread}
          onChange={setSpread}
          label="Spread it over payments"
          hideLabel
        />
      </SettingBlock>

      {spread && (
        <>
          {/* The chips answer for themselves, and clear when the typed number
              is not one of them — a set showing a choice nobody made says the
              row and the field disagree. */}
          <Choice
            label="How many payments?"
            appearance="chip"
            value={
              SUGGESTED_INSTALLMENTS.some((n) => String(n) === installments)
                ? installments
                : ''
            }
            options={SUGGESTED_INSTALLMENTS.map((n) => ({
              value: String(n),
              label: String(n),
            }))}
            onValueChange={setInstallments}
          />
          <Field
            label="Or another number"
            info="How many payments to spread it over. The figure per payment follows from it."
          >
            <TextInput
              type="number"
              inputMode="numeric"
              min={1}
              value={installments}
              aria-describedby={refused ? planId : undefined}
              aria-invalid={refused !== null || undefined}
              onChange={(e) => setInstallments(e.target.value)}
            />
          </Field>
          <Choice
            label="How often?"
            appearance="chip"
            value={cadence}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'annual', label: 'Yearly' },
            ]}
            onValueChange={(v) => setCadence(v as 'monthly' | 'annual')}
          />
          {/* When the first one falls. Today by default, because the ordinary
              answer is "start it now" — and a date is offered rather than
              assumed, since somebody pledging in Elul may mean the payments to
              begin after Yom Tov. */}
          <Field
            label="First payment"
            info="When the first installment falls. The rest follow at the cadence above."
          >
            <DateInput
              value={startsOn}
              min={today()}
              onChange={(e) => setStartsOn(e.target.value)}
            />
          </Field>
          {refused ? (
            <p className="pledge__fine" id={planId} role="alert">
              {refused}
            </p>
          ) : split ? (
            <div className="pledge__total">
              {/* The arithmetic, stated as the visitor reads it rather than as
                  the ledger stores it. A last payment that differs is said so
                  here, because it is the one figure the count does not give. */}
              <TotalLine
                label={`${count} ${cadence === 'annual' ? 'yearly' : 'monthly'} payments`}
                amount={money(split.rateCents)}
              />
              {split.lastCents !== split.rateCents && (
                <TotalLine
                  label="Last payment"
                  amount={money(split.lastCents)}
                />
              )}
              <TotalLine
                label="Total"
                amount={money(amountCents)}
                emphasis="total"
              />
            </div>
          ) : null}
        </>
      )}

      {/* Asked whether or not it is spread, because paying the first one now is
          what confirms a pledge without the office either way. */}
      {refused === null && (
        <Choice
          label="How would you like to start?"
          /* Moving the date past today takes the pay-now answer away, and the
             card is what it becomes: they said they would start it with a card,
             and the date is the only thing that changed. */
          value={startsLater && start === 'pay' ? 'card' : start}
          options={[
            ...(startsLater
              ? []
              : [
                  {
                    value: 'pay',
                    label: `Pay ${money(firstCents)} now`,
                    description: planned
                      ? 'We do not have to confirm it first, and your card is kept for the rest.'
                      : 'We do not have to confirm it first.',
                  },
                ]),
            {
              value: 'card',
              label: 'Leave a card for it',
              description:
                'Nothing is charged today. We confirm it and the payments start from there.',
            },
            {
              value: 'later',
              label: 'We will be in touch',
              description: 'No card now. We will call or write to arrange it.',
            },
          ]}
          onValueChange={(v) => setStart(v as 'pay' | 'card' | 'later')}
        />
      )}

      {challenge.data?.siteKey && (
        <Challenge
          siteKey={challenge.data.siteKey}
          onToken={setToken}
          spentAt={spentAt}
        />
      )}
      {pledge.error && <Notice tone="error">{pledge.error.message}</Notice>}

      <Button
        onClick={() => {
          if (!ready) return
          pledge.mutate({
            shortId: orgId,
            campaignShortId: campaignId,
            chargeItemId: chargeItemId,
            amountCents,
            forLabel,
            donorName: name.trim(),
            donorEmail: email.trim() || undefined,
            donorPhone: phone.trim() || undefined,
            plan: planned
              ? { cadence, installments: count, startsOn }
              : undefined,
            payFirstNow: payNow,
            leaveCard,
            replaces: left.current ?? undefined,
            challenge: token,
          })
        }}
        disabled={!ready || pledge.isPending}
      >
        {/* Where a card follows, this ends the answers and charges nothing,
            so it names no amount; otherwise it IS the pledge. */}
        {opensCheckout
          ? continueLabel(pledge.isPending)
          : pledge.isPending
            ? 'Sending…'
            : `Pledge ${money(amountCents)}`}
      </Button>
      <Button variant="quiet" onClick={onCancel}>
        Never mind
      </Button>
      <p className="pledge__fine">
        {opensCheckout
          ? 'Next, your card. Nothing is charged yet.'
          : 'Nothing is charged now. We will be in touch to arrange it.'}
      </p>
      {payment}
    </div>
  )
}
